import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Progress } from "antd";
import { privacyPolicyUrl } from "../PublicSurvey/ConsentCheckbox";
import {
  baseVideoType,
  canRecord,
  formatSeconds,
  pickRecorderType,
  requestVideoUpload,
  uploadInChunks,
  videoDurationLimits,
} from "./videoUpload";
import styles from "./VideoAnswer.module.scss";

const COUNTDOWN_SECONDS = 3;
// About 2.5 Mbit/s: a 90-second answer stays near 30 MB.
const VIDEO_BITS_PER_SECOND = 2500000;
const DEFAULT_MAX_BYTES = 150 * 1024 * 1024;

// Refusals from POST /feedback/public/video-upload the respondent can act on.
const UPLOAD_REFUSALS = {
  captcha_expired: "surveyManagement.video.error_session",
  rate_limited: "surveyManagement.video.error_rate_limited",
  video_too_large: "surveyManagement.video.error_too_large",
  video_type: "surveyManagement.video.error_type",
  video_disabled: "surveyManagement.video.error_unavailable",
  video_unavailable: "surveyManagement.video.error_unavailable",
};

/**
 * Chrome's recorded WebM has no duration in its header, so the player shows 0:00 and a black frame
 * until it is played. Seeking far ahead makes the browser read the whole clip and learn its length;
 * then a seek to the start shows the first frame.
 */
export const showFirstFrame = (event) => {
  const player = event.currentTarget;
  const toStart = () => {
    player.removeEventListener("timeupdate", toStart);
    player.currentTime = 0.1;
  };
  if (Number.isFinite(player.duration)) {
    player.currentTime = 0.1;
    return;
  }
  player.addEventListener("timeupdate", toStart);
  player.currentTime = Number.MAX_SAFE_INTEGER;
};

/**
 * The answer to a video question on the public survey page: consent, then the camera (3-second
 * countdown, time left, stops at the maximum), a preview to retake or send, and the upload with a
 * progress bar. Only a recording counts: there is no file upload, so without a camera (refused, or a
 * browser that can't record) the respondent is asked to allow it or open the link in another
 * browser. The answer becomes {video_ref, duration_seconds} once the upload finishes.
 *
 * ``upload`` comes from the public page: {slug, captchaPass, company, retentionDays, contactEmail,
 * maxBytes, enabled}. Without it (the builder preview) the question shows a static placeholder.
 */
const VideoAnswer = ({ question, value, onChange, upload, language, labelledBy }) => {
  const { t } = useTranslation();
  const { min, max } = videoDurationLimits(question);
  const [consent, setConsent] = useState(false);
  // consent → camera → countdown → recording → preview → uploading → done; "blocked" after a camera problem.
  const [stage, setStage] = useState(value?.video_ref ? "done" : "consent");
  const [cameraProblem, setCameraProblem] = useState("");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [elapsed, setElapsed] = useState(0);
  const [clip, setClip] = useState(null); // {blob, url, duration, mime}
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const streamRef = useRef(null);
  const liveRef = useRef(null);
  const recorderRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const sessionRef = useRef(null); // {uploadUrl, videoRef, chunkBytes, blob} for a resume
  const maxBytes = upload?.maxBytes || DEFAULT_MAX_BYTES;

  const stopCamera = useCallback(() => {
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(
    () => () => {
      clearTimer();
      stopCamera();
    },
    [stopCamera]
  );

  useEffect(
    () => () => {
      if (clip?.url) URL.revokeObjectURL(clip.url);
    },
    [clip]
  );

  useEffect(() => {
    if (liveRef.current && streamRef.current && ["camera", "countdown", "recording"].includes(stage)) {
      liveRef.current.srcObject = streamRef.current;
    }
  }, [stage]);

  if (!upload) {
    return (
      <div className={styles.placeholder}>
        <span className={styles.placeholderIcon} aria-hidden="true">
          ●
        </span>
        {t("surveyManagement.video.placeholder", { min: formatSeconds(min), max: formatSeconds(max) })}
      </div>
    );
  }

  if (upload.enabled === false) {
    return <p className={styles.error}>{t("surveyManagement.video.error_unavailable")}</p>;
  }

  const openCamera = async () => {
    setError("");
    if (!canRecord() || pickRecorderType() === null) {
      setCameraProblem(t("surveyManagement.video.camera_unsupported"));
      setStage("blocked");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      setStage("camera");
    } catch (_) {
      setCameraProblem(t("surveyManagement.video.camera_denied"));
      setStage("blocked");
    }
  };

  const finishRecording = (chunks, mime) => {
    clearTimer();
    const duration = (Date.now() - startedAtRef.current) / 1000;
    const blob = new Blob(chunks, { type: mime || "video/webm" });
    stopCamera();
    setClip({ blob, url: URL.createObjectURL(blob), duration: Math.min(duration, max), mime: blob.type });
    setStage("preview");
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const type = pickRecorderType();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(type ? { mimeType: type } : {}),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      });
    } catch (_) {
      recorder = new MediaRecorder(stream);
    }
    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) chunks.push(event.data);
    };
    recorder.onstop = () => finishRecording(chunks, recorder.mimeType || type);
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    setElapsed(0);
    recorder.start(1000);
    setStage("recording");
    timerRef.current = setInterval(() => {
      const seconds = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(seconds);
      if (seconds >= max && recorder.state === "recording") recorder.stop();
    }, 250);
  };

  const startCountdown = () => {
    setCountdown(COUNTDOWN_SECONDS);
    setStage("countdown");
    let left = COUNTDOWN_SECONDS;
    timerRef.current = setInterval(() => {
      left -= 1;
      setCountdown(left);
      if (left <= 0) {
        clearTimer();
        startRecording();
      }
    }, 1000);
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === "recording") recorder.stop();
  };

  const retake = () => {
    setClip(null);
    setError("");
    setProgress(0);
    sessionRef.current = null;
    onChange(null);
    openCamera();
  };

  const send = async () => {
    if (!clip) return;
    setError("");
    setStage("uploading");
    try {
      let session = sessionRef.current;
      if (!session || session.blob !== clip.blob) {
        const mime = baseVideoType(clip.mime) || "video/webm";
        const { ok, body } = await requestVideoUpload({
          slug: upload.slug,
          question_id: question.id,
          mime,
          size: clip.blob.size,
          consent: true,
          captcha_pass: upload.captchaPass || undefined,
          kiosk_key: upload.kioskKey || undefined,
          start_consent: upload.startConsent || undefined,
          start_token: upload.startToken || undefined,
        });
        if (!ok || !body?.started) {
          setError(
            t(UPLOAD_REFUSALS[body?.reason] || "surveyManagement.video.error_upload", {
              mb: Math.round(maxBytes / 1048576),
            })
          );
          setStage("preview");
          return;
        }
        session = { uploadUrl: body.upload_url, videoRef: body.video_ref, chunkBytes: body.chunk_bytes, blob: clip.blob };
        sessionRef.current = session;
      }
      await uploadInChunks({
        uploadUrl: session.uploadUrl,
        blob: session.blob,
        chunkBytes: session.chunkBytes,
        onProgress: setProgress,
      });
      onChange({ video_ref: session.videoRef, duration_seconds: Math.round(clip.duration * 10) / 10 });
      setStage("done");
    } catch (_) {
      setError(t("surveyManagement.video.error_upload_retry"));
      setStage("failed");
    }
  };

  const tooShort = clip && clip.duration < min;
  return (
    <div className={styles.wrap} aria-labelledby={labelledBy}>
      {question.videoInstructions ? <p className={styles.instructions}>{question.videoInstructions}</p> : null}
      <p className={styles.limits}>
        {t("surveyManagement.video.limits", { min: formatSeconds(min), max: formatSeconds(max) })}
      </p>

      {stage === "consent" ? (
        <>
          <Checkbox
            className={styles.consent}
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            aria-required="true"
          >
            {t(upload.company ? "surveyManagement.video.consent_company" : "surveyManagement.video.consent_generic", {
              company: upload.company,
              days: upload.retentionDays || 90,
            })}
            {upload.contactEmail ? ` ${t("surveyManagement.video.consent_delete", { email: upload.contactEmail })}` : ""}{" "}
            <a
              href={privacyPolicyUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {t("surveyManagement.video.privacy_notice")}
            </a>
          </Checkbox>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} disabled={!consent} onClick={openCamera}>
              {t("surveyManagement.video.open_camera")}
            </button>
          </div>
        </>
      ) : null}

      {["camera", "countdown", "recording"].includes(stage) ? (
        <div className={styles.stage}>
          <video ref={liveRef} className={styles.live} autoPlay muted playsInline />
          {stage === "countdown" ? (
            <div className={styles.countdown} aria-live="assertive">
              {countdown}
            </div>
          ) : null}
          {stage === "recording" ? (
            <div className={styles.recordingBadge} aria-live="polite">
              <span className={styles.recDot} aria-hidden="true" />
              {t("surveyManagement.video.time_left", { time: formatSeconds(max - elapsed) })}
            </div>
          ) : null}
          <div className={styles.actions}>
            {stage === "camera" ? (
              <button type="button" className={styles.primary} onClick={startCountdown}>
                {t("surveyManagement.video.start")}
              </button>
            ) : null}
            {stage === "recording" ? (
              <button type="button" className={styles.stop} onClick={stopRecording}>
                {elapsed < min
                  ? t("surveyManagement.video.stop_early", { time: formatSeconds(min - elapsed) })
                  : t("surveyManagement.video.stop")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {stage === "blocked" ? (
        <div className={styles.fileBox}>
          {cameraProblem ? <p className={styles.notice}>{cameraProblem}</p> : null}
          <button type="button" className={styles.primary} onClick={openCamera}>
            {t("surveyManagement.video.try_camera_again")}
          </button>
        </div>
      ) : null}

      {["preview", "uploading", "failed", "done"].includes(stage) && clip ? (
        <div className={styles.stage}>
          <video
            className={styles.playback}
            src={clip.url}
            controls
            playsInline
            preload="auto"
            onLoadedMetadata={showFirstFrame}
          />
          <p className={stage === "preview" ? styles.ready : styles.length}>
            {stage === "preview"
              ? t("surveyManagement.video.recorded_ready", { time: formatSeconds(clip.duration) })
              : t("surveyManagement.video.length", { time: formatSeconds(clip.duration) })}
          </p>
        </div>
      ) : null}

      {stage === "preview" ? (
        <>
          {tooShort ? (
            <p className={styles.error} role="alert">
              {t("surveyManagement.video.too_short", { min: formatSeconds(min) })}
            </p>
          ) : null}
          <div className={styles.actions}>
            <button type="button" className={styles.primary} disabled={tooShort} onClick={send}>
              {t("surveyManagement.video.send")}
            </button>
            <button type="button" className={styles.link} onClick={retake}>
              {t("surveyManagement.video.retake")}
            </button>
          </div>
        </>
      ) : null}

      {stage === "uploading" || stage === "failed" ? (
        <div className={styles.progress}>
          <Progress percent={Math.floor(progress * 100)} status={stage === "failed" ? "exception" : "active"} />
          <p className={styles.hint}>
            {stage === "uploading" ? t("surveyManagement.video.uploading") : null}
          </p>
          {stage === "failed" ? (
            <div className={styles.actions}>
              <button type="button" className={styles.primary} onClick={send}>
                {t("surveyManagement.video.retry")}
              </button>
              <button type="button" className={styles.link} onClick={retake}>
                {t("surveyManagement.video.retake")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {stage === "done" ? (
        <div className={styles.done}>
          <p className={styles.success} role="status">
            {t("surveyManagement.video.uploaded", { time: formatSeconds(value?.duration_seconds || clip?.duration) })}
          </p>
          <button type="button" className={styles.link} onClick={retake}>
            {t("surveyManagement.video.replace")}
          </button>
        </div>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

    </div>
  );
};

export default VideoAnswer;
